import { AuthType } from '@/services/auth/types';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useIndexPage } from '@/hooks/useIndexPage';
import { usePageViewRecording } from '@/hooks/usePageViewRecording';
import { usePopover } from '@/hooks/usePopover';
import { useTrialStatus } from '@/hooks/useTrialStatus';
import { TenantUrlSync } from '@/services/tenants/TenantUrlSync';
import { useTenants } from '@/services/tenants/useTenants';
import { PhysnaLogo } from '@/svg/PhysnaLogo';
import { Settings } from '@mui/icons-material';
import { Box, Divider, IconButton, Link as MuiLink } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { Link } from 'react-router-dom';
import { StyledList, StyledListHeader, StyledListItem } from '../AssetsPage/PopoverList';
import { BodyText } from '../BodyText';
import { RoundedPopover, popoverRightProperties } from '../Popover/RoundedPopover';
import { TrialGuard } from '../TrialGuard';
import { AccountButton } from './AccountButton';
import { HelpButton } from './HelpButton';
import { Navigation } from './Navigation';
import { TenantSelector } from './TenantSelector';
import { TrialStatus } from './TrialStatus';
import { HelpContent } from '../HelpContent/HelpContent';
import { ThemeModeSelector } from './ThemeModeSelector';

const PageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  backgroundColor: '#000000',
  color: '#ffffff',
});

const Header = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px',
  backgroundColor: '#000000',
  height: '62px',
});

const LogoLink = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  textDecoration: 'none',
});

const HeaderIcons = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const ContentWrapper = styled(Box)({
  display: 'flex',
  flex: 1,
  overflow: 'hidden',
  margin: '0 8px 8px 8px',
  gap: '10px',
  minHeight: 0,
});

const MainContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  overflow: 'auto',
  backgroundColor: theme.palette.background.default,
  borderRadius: '8px',
  minWidth: 0,
}));

const StyledIconButton = styled(IconButton)({
  color: '#ffffff',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
});

const StyledLink = styled(Link)({
  textDecoration: 'none',
  color: 'inherit',
});

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getPath } = useTenants();
  const { permissions, isLoading } = useGetCurrentUser();
  const { isTaxonomyEnabled } = useFeatureFlags();
  const { indexPagePath } = useIndexPage();
  const { open, close, isOpen, anchorElement } = usePopover();
  const trialStatus = useTrialStatus();
  // The /tenants/:id/users API routes are 404 under the 'cn' auth strategy
  // (see packages/api-server/src/auth/strategies/cn-auth.ts: tenantUsers: false),
  // so hide the Users menu entry entirely when cn is active.
  //
  // IMPORTANT: do NOT write `=== 'cn'` here. Vite replaces import.meta.env.VITE_*
  // with a build-time string literal, and Terser then folds
  // `"VITE_RUNTIME_API_AUTH_STRATEGY" === 'cn'` to `false`, dropping the placeholder
  // from the output so docker-entrypoint's runtime sed substitution has nothing to
  // target. The switch-with-returns-and-default-throw pattern below matches the one
  // in AuthProvider.tsx that's proven to survive minification, because Terser
  // preserves switches whose default branch has an observable side effect (throw).
  const isCnAuth = ((): boolean => {
    const authType: AuthType = import.meta.env.VITE_API_AUTH_STRATEGY;
    switch (authType) {
      case 'cn': {
        return true;
      }
      case 'cognito':
      case 'oidc-federated':
      case 'local': {
        return false;
      }
      default: {
        const _exhaustive: never = authType;
        throw new Error(`Unsupported auth type: ${_exhaustive}`);
      }
    }
  })();

  usePageViewRecording();

  return (
    <PageContainer>
      <TenantUrlSync />
      <Header>
        <Box display='flex' alignItems='center' gap='16px' flex='1' minWidth={0}>
          <LogoLink to={indexPagePath}>
            <PhysnaLogo size={3.5} textColor='#ffffff' dotColor='#387755' title='Physna' />
          </LogoLink>
          <TenantSelector />
          <TrialStatus />
        </Box>
        <HeaderIcons>
          <AccountButton />
          <StyledIconButton onClick={open}>
            <Settings />
          </StyledIconButton>
          <HelpButton />
        </HeaderIcons>
        <RoundedPopover open={isOpen} anchorEl={anchorElement} onClose={close} {...popoverRightProperties}>
          <StyledList sx={{ minWidth: '160px' }}>
            <StyledListHeader>
              <BodyText>System</BodyText>
            </StyledListHeader>
            <Divider />
            {permissions.canManageUsers && !isCnAuth && (
              <StyledLink to={getPath('users')} style={{ textDecoration: 'none', color: 'inherit' }} onClick={close}>
                <StyledListItem>
                  <BodyText>Users</BodyText>
                </StyledListItem>
              </StyledLink>
            )}
            {permissions.canManageMetadataFields && (
              <StyledLink to={getPath('metadata')} style={{ textDecoration: 'none', color: 'inherit' }} onClick={close}>
                <StyledListItem>
                  <BodyText>Metadata Fields</BodyText>
                </StyledListItem>
              </StyledLink>
            )}
            {permissions.canAddMetadataFields && permissions.canModifyAssetMetadata && (
              <StyledLink
                to={getPath('metadata-upload')}
                style={{ textDecoration: 'none', color: 'inherit' }}
                onClick={close}
              >
                <StyledListItem>
                  <BodyText>Metadata Upload</BodyText>
                </StyledListItem>
              </StyledLink>
            )}
            {permissions.canManageTaxonomy && isTaxonomyEnabled && (
              <StyledLink to={getPath('taxonomy')} style={{ textDecoration: 'none', color: 'inherit' }} onClick={close}>
                <StyledListItem>
                  <BodyText>Taxonomy</BodyText>
                </StyledListItem>
              </StyledLink>
            )}
            <StyledLink
              to='/changelog'
              style={{
                textDecoration: 'none',
              }}
              onClick={close}
            >
              <StyledListItem>
                <BodyText>Changelog</BodyText>
              </StyledListItem>
            </StyledLink>
            <MuiLink
              href='mailto:support@physna.com'
              target='_blank'
              style={{
                textDecoration: 'none',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
              onClick={close}
            >
              <StyledListItem>
                <BodyText>Email Support</BodyText>
              </StyledListItem>
            </MuiLink>
            <MuiLink
              href='/privacy-policy.pdf'
              target='_blank'
              style={{
                textDecoration: 'none',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
              onClick={close}
            >
              <StyledListItem>
                <BodyText>Privacy Policy</BodyText>
              </StyledListItem>
            </MuiLink>
            <Divider />
            <ThemeModeSelector />
          </StyledList>
        </RoundedPopover>
      </Header>
      <ContentWrapper>
        <MainContent>
          {!isLoading && !permissions.canOnlySearch && !trialStatus.isExpired && <Navigation />}
          {!isLoading && <TrialGuard>{children}</TrialGuard>}
        </MainContent>
        <HelpContent />
      </ContentWrapper>
    </PageContainer>
  );
};
